/**
 * kd-algolia-carousel: v2 TOP 商品カルーセル（1セクション=1ランキング）
 *
 * sections/v2-top-product-carousel.liquid から attribute で設定値を受け取り、
 * Algolia 検索結果を Liquid 側コンテナ（[data-hits-container]）に注入する。
 *
 * Lit は振る舞いレイヤーに徹し HTML 構造は Liquid が担う。
 * - render() は nothing を返す
 * - <li> ラッパーとカード構造は Liquid 側 <template data-hit-template> から cloneNode
 * - hit 値は data-field="..." セレクタを介して DOM テキスト/属性に流し込む
 *   （流し込みロジックは carousel.logic.ts の applyDisplayDataToFragment に集約）
 */
import { LitElement, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import type { AlgoliaHit } from './algolia-hit-template';
import {
  getAlgoliaConfig,
  createSearchClient,
  waitForAlgoliaLibs,
} from './utils/algolia-client';
import {
  buildSearchParams,
  buildIndexName,
  applyDisplayDataToFragment,
  normalizeProductVisibilityPolicy,
  type CarouselSettings,
} from './carousel.logic';

interface SearchResult {
  hits: AlgoliaHit[];
  nbHits: number;
}

@customElement('kd-algolia-carousel')
export class KdAlgoliaCarousel extends LitElement {
  createRenderRoot() {
    return this;
  }

  @property({ type: String, attribute: 'sort-index' })
  sortIndex = '';

  @property({ type: String, attribute: 'collection-id' })
  collectionId = '';

  @property({ type: String })
  filters = '';

  @property({ type: String, attribute: 'rule-contexts' })
  ruleContexts = '';

  @property({ type: Number, attribute: 'display-num' })
  displayNum = 10;

  @property({ type: String, attribute: 'visibility-policy' })
  visibilityPolicy = 'default';

  @property({ type: Boolean, attribute: 'show-rank' })
  showRank = false;

  // TOP ページなど「割引商品でも比較価格を出したくない」view 側コンテキスト向け。
  // 通常は割引判定（compare_at_price > price）に従って自動表示されるが、
  // この属性が立っている場合は表示判定をバイパスして常時非表示にする。
  @property({ type: Boolean, attribute: 'hide-compare-price' })
  hideComparePrice = false;

  // true なら自前で Algolia へ search() せず、外部から applyExternalHits() で
  // hits を注入される運用に切り替わる。複数 carousel を 1 リクエストに集約したい
  // ケース向け（並列リクエストによる課金倍増を避ける）。失敗時は
  // handleOrchestratorFailure() で section を非表示にして並列 fallback を防ぐ。
  @property({ type: Boolean, attribute: 'data-deferred', reflect: true })
  deferred = false;

  private _loaded = false;

  async connectedCallback(): Promise<void> {
    super.connectedCallback();
    // Shopify テーマエディタで section をドラッグ移動すると connectedCallback が
    // 再発火するため二重起動を防ぐ
    if (this._loaded) return;
    this._loaded = true;
    // deferred モードなら自前 search せず orchestrator からの通知を待つ
    if (this.deferred) return;
    await this._load();
  }

  /**
   * 自身の Algolia クエリ設定を CarouselSettings 形で返す public API。
   * 外部 batch 機構が複数 carousel の設定をまとめてクエリ化する際の入力源。
   */
  getQueryConfig(): CarouselSettings {
    return {
      sortIndex: this.sortIndex,
      collectionId: this.collectionId,
      filters: this.filters,
      ruleContexts: this.ruleContexts,
      displayNum: this.displayNum,
      visibilityPolicy: normalizeProductVisibilityPolicy(this.visibilityPolicy),
    };
  }

  /**
   * 外部から hits を受け取り、既存の _renderHits パスに合流させる public API。
   * 0 件のときは自前 _load() と同じく section を非表示にする。
   */
  applyExternalHits(hits: AlgoliaHit[], nbHits?: number): void {
    if (hits.length === 0) {
      this._hideSection();
      return;
    }
    this._renderHits(hits);
    this._renderCount(nbHits);
  }

  /**
   * 外部 batch 機構が失敗した際の通知 public API。
   * 自前 search に fallback すると並列で Algolia リクエストが走り
   * 課金倍増の元凶が再発するため、failure 時は section ごと非表示にする。
   */
  handleOrchestratorFailure(): void {
    this._hideSection();
  }

  private async _load(): Promise<void> {
    const config = getAlgoliaConfig();
    if (!config?.connected) {
      this._hideSection();
      return;
    }

    try {
      await waitForAlgoliaLibs();
      const searchClient = createSearchClient({
        cacheKey: 'algolia-responses-carousel',
        maxEntries: 50,
      });
      if (!searchClient) {
        this._hideSection();
        return;
      }

      const params = buildSearchParams(
        this.getQueryConfig(),
        config.customer,
      );

      const response = (await searchClient.search([
        {
          indexName: buildIndexName(config.indexName, this.sortIndex),
          params,
        },
      ])) as { results: SearchResult[] };

      const hits = response.results[0]?.hits ?? [];
      this._renderHits(hits);
      this._renderCount(response.results[0]?.nbHits);
    } catch {
      this._hideSection();
    }
  }

  /**
   * 総件数（nbHits）を見出し右「全N商品」と末尾ボタン「（N商品）」へ流し込む。
   * nbHits 不明（外部機構が渡さない）のときは何もしない（プレースホルダは hidden のまま）。
   */
  private _renderCount(nbHits?: number): void {
    if (typeof nbHits !== 'number' || nbHits <= 0) return;
    const formatted = nbHits.toLocaleString('ja-JP');
    const fill = (wrapSel: string, numSel: string): void => {
      const wrap = this.closest<HTMLElement>('[data-carousel-root]')?.querySelector<HTMLElement>(wrapSel);
      const num = wrap?.querySelector<HTMLElement>(numSel);
      if (wrap && num) {
        num.textContent = formatted;
        wrap.hidden = false;
      }
    };
    fill('[data-field="result-count"]', '[data-field="result-count-num"]');
    fill('[data-field="more-count"]', '[data-field="more-count-num"]');
  }

  private _renderHits(hits: AlgoliaHit[]): void {
    const tpl = this.querySelector<HTMLTemplateElement>('[data-hit-template]');
    const container = this.querySelector<HTMLElement>('[data-hits-container]');
    if (!tpl || !container) return;

    if (hits.length === 0) {
      this._hideSection();
      return;
    }

    // [data-more-link-sentinel] は grid モードのセクション（現在 v2-page-product-carousel /
    // v2-top-recommended）が <ul> 末尾に静的配置する「もっと見る」ボタン用 <li>。
    // grid の最後のセルとして商品カードと同じ幅で表示するため <ul> 内に存在する必要があり、
    // 全消去すると消えてしまう。sentinel 以外の子を remove し、hits を sentinel の前に
    // insertBefore することで sentinel が常に grid 末尾に残る。
    // （v2-top-product-carousel は Phase 4 で slider 化し「すべて見る」ボタンを <ul> 外・
    //   カルーセル直下へ移したため sentinel を持たない。その場合 sentinel は null となり
    //   下記 else 分岐で appendChild される。）
    const sentinel = container.querySelector<HTMLElement>('[data-more-link-sentinel]');
    Array.from(container.children).forEach((child) => {
      if (child !== sentinel) child.remove();
    });
    hits.forEach((hit, i) => {
      const fragment = tpl.content.cloneNode(true) as DocumentFragment;
      applyDisplayDataToFragment(fragment, hit, i, {
        showRank: this.showRank,
        hideComparePrice: this.hideComparePrice,
      });
      if (sentinel) {
        container.insertBefore(fragment, sentinel);
      } else {
        container.appendChild(fragment);
      }
    });
  }

  private _hideSection(): void {
    // hit 0 件 / 接続失敗時はセクション全体を非表示にする（見出しだけ残るのを防ぐ）
    // Liquid 側 <section data-carousel-root> を data-* 属性で参照（class 名変更に強い疎結合）
    const section = this.closest<HTMLElement>('[data-carousel-root]');
    if (section) section.hidden = true;
  }

  render() {
    return nothing;
  }
}
